import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { storage, auth, functions } from "./firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { v4 as uuidv4 } from "uuid";
import DashboardLayout from "./DashboardLayout";

const REQUIRED_KEYS = [
  "front",
  "frontTopLeft",
  "frontTopRight",
  "frontBottomLeft",
  "frontBottomRight",
  "back",
  "backTopLeft",
  "backTopRight",
  "backBottomLeft",
  "backBottomRight",
];

const getFriendlyLabel = (key) => {
  const map = {
    front: "Front",
    back: "Back",
    frontTopLeft: "Front Corner #1",
    frontTopRight: "Front Corner #2",
    frontBottomLeft: "Front Corner #3",
    frontBottomRight: "Front Corner #4",
    backTopLeft: "Back Corner #1",
    backTopRight: "Back Corner #2",
    backBottomLeft: "Back Corner #3",
    backBottomRight: "Back Corner #4",
    extra1: "Extra #1",
    extra2: "Extra #2",
    extra3: "Extra #3",
    extra4: "Extra #4",
    extra5: "Extra #5",
  };
  return map[key] || key;
};

// Client-side helpers to keep uploads within your rules
const MAX_BYTES = 10 * 1024 * 1024; // 10MB

function normalizeContentType(rawType, name) {
  // Normalize common variants and fall back to extension sniffing
  const t = (rawType || "").toLowerCase().trim();
  if (t === "image/jpg") return "image/jpeg";
  if (t.startsWith("image/")) return t;

  const lower = (name || "").toLowerCase();
  if (lower.endsWith(".png")) return "image/png";
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) return "image/jpeg";
  if (lower.endsWith(".heic") || lower.endsWith(".heif")) return "image/heic";

  return ""; // reject unknown types
}

function isAllowedImageType(ct) {
  return /^image\/(png|jpeg|heic|heif)$/.test(ct);
}

function SubmitForm() {
  const [cardName, setCardName] = useState("");
  const [cardIdentifier, setCardIdentifier] = useState("");
  const [images, setImages] = useState(() => {
    const base = {};
    REQUIRED_KEYS.forEach((key) => (base[key] = null));
    return base;
  });
  const [extraKeys, setExtraKeys] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const navigate = useNavigate();

  const handleImageChange = (key, file) => {
    setImages((prev) => ({ ...prev, [key]: file || null }));
  };

  const handleImageRemove = (key) => {
    setImages((prev) => ({ ...prev, [key]: null }));
  };

  const addExtraImage = () => {
    if (extraKeys.length >= 5) return;
    const newKey = `extra${extraKeys.length + 1}`;
    setExtraKeys((prev) => [...prev, newKey]);
    setImages((prev) => ({ ...prev, [newKey]: null }));
  };

  async function uploadImageForSubmission(tempSubmissionId, key, file) {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");

    if (!file) throw new Error(`No file selected for ${getFriendlyLabel(key)}.`);

    // Size/type checks to avoid rule rejections
    if (file.size > MAX_BYTES) {
      throw new Error(`${getFriendlyLabel(key)} exceeds the 10MB limit.`);
    }
    const contentType = normalizeContentType(file.type, file.name);
    if (!isAllowedImageType(contentType)) {
      throw new Error(
        `${getFriendlyLabel(key)} must be PNG, JPEG, or HEIC. Detected: ${
          contentType || "unknown"
        }`
      );
    }

    const filename = `${uuidv4()}_${key}`;
    // IMPORTANT: path that matches your Storage rules
    const path = `users/${user.uid}/submissions/${tempSubmissionId}/${filename}`;
    const imageRef = ref(storage, path);

    try {
      await uploadBytes(imageRef, file, { contentType });
    } catch (err) {
      // Surface CORS/authorization clues clearly to the user
      const msg = String(err?.message || err || "");
      if (msg.toLowerCase().includes("cors")) {
        throw new Error(
          `Upload blocked by CORS. Update the bucket CORS to allow https://whatgradewillitget.com and required x-goog-* headers.`
        );
      }
      if (msg.toLowerCase().includes("unauthorized") || msg.includes("403")) {
        throw new Error(
          `Upload not authorized by Storage rules for path ${path}. Check rules for users/{uid}/submissions/** and contentType.`
        );
      }
      throw err;
    }

    const url = await getDownloadURL(imageRef);
    return { key, url, path };
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const requiredMissing = REQUIRED_KEYS.some((key) => !images[key]);
    if (requiredMissing) {
      alert("Please upload all 10 required images.");
      return;
    }

    const user = auth.currentUser;
    if (!user || !user.uid) {
      alert("You must be logged in to submit a card. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    setUploadCount(0);

    // Temporary client-side submission group id used ONLY for Storage foldering
    const tempSubmissionId = uuidv4();

    try {
      console.log("📤 Uploading images...");

      const entries = Object.entries(images);
      const uploaded = {};
      // Upload sequentially for clearer progress & simpler error handling
      for (const [key, file] of entries) {
        if (!file) continue; // skip optional extras not chosen
        try {
          console.log(`Uploading ${getFriendlyLabel(key)}...`);
          const { url } = await uploadImageForSubmission(
            tempSubmissionId,
            key,
            file
          );
          uploaded[key] = url; // callable will accept URLs
          setUploadCount((prev) => prev + 1);
          console.log(`${getFriendlyLabel(key)} uploaded successfully.`);
        } catch (err) {
          console.error(`Failed to upload ${getFriendlyLabel(key)}:`, err);
          // Stop immediately; user action needed
          throw new Error(
            `Error uploading ${getFriendlyLabel(key)}: ${err.message}`
          );
        }
      }

      // Call backend to atomically deduct credit + create submission
      console.log("🧾 Creating submission (atomic with credit deduction)...");
      const createFn = httpsCallable(
        functions,
        "deductCreditAndCreateSubmission"
      );
      const res = await createFn({
        cardName,
        cardNumber: cardIdentifier, // required by backend
        images: uploaded, // backend accepts storage paths or signed URLs; we pass URLs
      });

      if (!res?.data?.ok) {
        throw new Error("Server did not confirm submission creation.");
      }

      alert("Card submitted successfully!");
      setCardName("");
      setCardIdentifier("");
      setImages(() => {
        const reset = {};
        REQUIRED_KEYS.forEach((k) => (reset[k] = null));
        extraKeys.forEach((k) => (reset[k] = null));
        return reset;
      });
      setExtraKeys([]);
      navigate("/orders");
    } catch (error) {
      console.error("Submission error:", error);
      alert("Error submitting card: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="sm:hidden flex justify-start px-6 pt-6">
        <button
          onClick={() => navigate("/user-dashboard")}
          className="bg-[#1976d2] text-white text-sm font-bold font-[Montserrat] px-5 py-2 rounded-md"
        >
          ← Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-2 mt-2 text-center">Submit a Card</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.inputRow}>
          <label style={styles.inlineLabel}>Card Name:</label>
          <input
            type="text"
            placeholder="e.g. Charizard Holo"
            value={cardName}
            onChange={(e) => setCardName(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <div style={styles.inputRow}>
          <label style={styles.inlineLabel}>Identifier:</label>
          <input
            type="text"
            placeholder="e.g. #045/102 or Jersey #12"
            value={cardIdentifier}
            onChange={(e) => setCardIdentifier(e.target.value)}
            required
            style={styles.input}
          />
        </div>

        <div style={styles.grid}>
          {[...REQUIRED_KEYS, ...extraKeys].map((key) => (
            <label
              key={key}
              style={{ ...styles.imageBox, gridColumn: `span 1` }}
              htmlFor={key}
            >
              {images[key] ? (
                <>
                  <img
                    src={URL.createObjectURL(images[key])}
                    alt={key}
                    style={styles.image}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageRemove(key)}
                    style={styles.removeBtn}
                    aria-label={`Remove ${getFriendlyLabel(key)}`}
                    title={`Remove ${getFriendlyLabel(key)}`}
                  >
                    ✕
                  </button>
                </>
              ) : (
                <>
                  <div style={styles.uploadLabel}>+</div>
                  <input
                    id={key}
                    type="file"
                    accept="image/png, image/jpeg, image/heic, image/heif"
                    onChange={(e) => handleImageChange(key, e.target.files?.[0] || null)}
                    style={{ display: "none" }}
                  />
                </>
              )}
              <small style={{ fontSize: "12px", marginTop: "6px", color: "#aaa" }}>
                {getFriendlyLabel(key)}
              </small>
            </label>
          ))}
        </div>

        {extraKeys.length < 5 && (
          <button type="button" onClick={addExtraImage} style={styles.addBtn}>
            + Add Extra Photo
          </button>
        )}

        <button
          type="submit"
          style={{
            ...styles.submitButton,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              Submitting
              <span style={styles.spinner}></span>
            </>
          ) : (
            "Submit"
          )}
        </button>

        {isSubmitting && (
          <div style={{ color: "white", marginTop: "10px" }}>
            Uploaded {uploadCount} of {Object.values(images).filter(Boolean).length} images...
          </div>
        )}
      </form>
    </DashboardLayout>
  );
}

const styles = {
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    alignItems: "center",
    marginTop: "20px",
    minHeight: "calc(100vh - 100px)",
  },
  inputRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    width: "100%",
    maxWidth: "600px",
  },
  inlineLabel: {
    fontWeight: "bold",
    color: "white",
    fontSize: "16px",
    width: "110px",
    textAlign: "right",
  },
  input: {
    padding: "10px",
    width: "300px",
    fontSize: "16px",
    backgroundColor: "#000",
    color: "#fff",
    border: "1px solid #fff",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
    width: "100%",
    maxWidth: "900px",
    justifyContent: "center",
    margin: "0 auto",
  },
  imageBox: {
    position: "relative",
    width: "100%",
    aspectRatio: "1 / 1",
    border: "2px dashed #ccc",
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "32px",
    color: "#888",
    overflow: "hidden",
    textAlign: "center",
    cursor: "pointer",
  },
  uploadLabel: {
    fontSize: "40px",
    color: "#1976d2",
    display: "block",
    lineHeight: "1",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  removeBtn: {
    position: "absolute",
    top: "4px",
    right: "4px",
    background: "#d32f2f",
    color: "white",
    border: "none",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    cursor: "pointer",
    fontSize: "14px",
    lineHeight: "24px",
    textAlign: "center",
  },
  addBtn: {
    padding: "10px 20px",
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  submitButton: {
    padding: "10px 20px",
    backgroundColor: "#1976d2",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
  },
  spinner: {
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    width: "16px",
    height: "16px",
    animation: "spin 0.6s linear infinite",
  },
};

export default SubmitForm;
